import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type JSX,
  type ReactNode,
  type ChangeEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  FileSpreadsheet,
  FileText,
  FileOutput,
  File as FileIcon,
  Check,
  RefreshCw,
  AlertTriangle,
  Clock,
  ExternalLink,
  RotateCcw,
  Trash2,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Tag,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import * as XLSX from 'xlsx';
import type { PostgrestError } from '@supabase/supabase-js';
import { useApp } from '../hooks/useApp';
import { supabase } from '../lib/supabase';
import { createNotification } from '../lib/notifications';
import { detectSheets, useImport, type SheetMode } from '../hooks/useImport';
import { PROVENANCE_SOURCE_ALGOPIX_DISCOVERY } from '../lib/provenanceSources';
import { buildColumnIndex } from '../lib/algopix-columns';
import { extractEnvelope } from '../lib/extractEnvelope';
import ArbiSelect from '../components/ui/ArbiSelect';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import ImportConfigModal from '../components/files/ImportConfigModal';
import { Toast, type ToastState } from '../components/ui/Toast';

// ── Local types ───────────────────────────────────────────────────────────────

type TabKey =
  | 'new'
  | 'awaiting_prep'
  | 'ready'
  | 'sent'
  | 'output_received'
  | 'imported'
  | 'issues';

type FileTypeKey =
  | 'supplier_price_sheet'
  | 'algopix_output'
  | 'distributor_invoice'
  | 'po_document'
  | 'receiving_document'
  | 'other';

interface FileRow {
  id: string;
  display_name: string | null;
  storage_path: string | null;
  file_size: number | null;
  mime_type: string | null;
  file_type: string | null;
  lifecycle_status: string;
  uploaded_at: string | null;
  sent_to: string | null;
  sent_at: string | null;
  error_log: string | null;
  notes: string | null;
  related_import_id: string | null;
  related_po_id: string | null;
  po_number: string | null;
  import_processed_count: number | null;
}

interface PoOption {
  value: string;
  label: string;
}

type GenericSupabaseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
const callRpc = supabase.rpc.bind(supabase) as unknown as GenericSupabaseRpc;

// ── Constants ─────────────────────────────────────────────────────────────────

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'new', label: 'New' },
  { key: 'awaiting_prep', label: 'Awaiting Prep' },
  { key: 'ready', label: 'Ready' },
  { key: 'sent', label: 'Sent' },
  { key: 'output_received', label: 'Output Received' },
  { key: 'imported', label: 'Imported' },
  { key: 'issues', label: 'Issues' },
] as const;

const FILE_TYPE_OPTIONS: ReadonlyArray<{ value: FileTypeKey; label: string }> = [
  { value: 'supplier_price_sheet', label: 'Supplier Price Sheet' },
  { value: 'algopix_output', label: 'Algopix Output' },
  { value: 'distributor_invoice', label: 'Distributor Invoice' },
  { value: 'po_document', label: 'PO Document' },
  { value: 'receiving_document', label: 'Receiving Document' },
  { value: 'other', label: 'Other' },
] as const;

const FILE_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  FILE_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

const FILE_TYPE_COLORS: Record<string, string> = {
  supplier_price_sheet: 'bg-accent/15 text-accent',
  algopix_output: 'bg-info/15 text-info',
  distributor_invoice: 'bg-warning/15 text-warning',
  po_document: 'bg-success/15 text-success',
  receiving_document: 'bg-ink-muted/15 text-ink-muted',
  other: 'bg-ink-muted/15 text-ink-muted',
};

// ── Toast ─────────────────────────────────────────────────────────────────────

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatRelativeDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = Date.now();
  const diff = now - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function getFileIcon(fileType: string | null | undefined): LucideIcon {
  switch (fileType) {
    case 'supplier_price_sheet':
      return FileSpreadsheet;
    case 'algopix_output':
      return FileOutput;
    case 'distributor_invoice':
      return FileText;
    case 'po_document':
      return FileText;
    case 'receiving_document':
      return FileText;
    default:
      return FileIcon;
  }
}

function FileTypeBadge({ fileType }: { fileType: string | null | undefined }): JSX.Element | null {
  if (!fileType) return null;
  return (
    <span
      className={clsx(
        'px-1.5 py-0.5 rounded text-2xs font-medium',
        FILE_TYPE_COLORS[fileType] || FILE_TYPE_COLORS.other
      )}
    >
      {FILE_TYPE_LABELS[fileType] || fileType}
    </span>
  );
}

// ── Skeleton card ─────────────────────────────────────────────────────────────

function SkeletonCard(): JSX.Element {
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4 flex items-center gap-4">
      <div className="w-8 h-8 rounded bg-surface-2 skeleton" />
      <div className="flex-1 space-y-2">
        <div className="w-48 h-3.5 rounded bg-surface-2 skeleton" />
        <div className="w-32 h-3 rounded bg-surface-2 skeleton" />
      </div>
      <div className="w-24 h-7 rounded bg-surface-2 skeleton" />
    </div>
  );
}

// ── File card base ────────────────────────────────────────────────────────────

interface FileCardProps {
  file: FileRow;
  children?: ReactNode;
}

function FileCard({ file, children }: FileCardProps): JSX.Element {
  const Icon = getFileIcon(file.file_type);
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-4">
      <div className="flex items-center gap-4">
        <Icon size={32} className="text-ink-muted flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink-bright truncate">{file.display_name}</div>
          <div className="flex items-center gap-3 mt-1 text-xs text-ink-muted">
            {file.file_size != null && file.file_size > 0 && (
              <span className="font-mono">{formatFileSize(file.file_size)}</span>
            )}
            <span className="font-mono">{formatRelativeDate(file.uploaded_at)}</span>
            {file.file_type && <FileTypeBadge fileType={file.file_type} />}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">{children}</div>
      </div>
    </div>
  );
}

// ── Tab-specific card renderers ───────────────────────────────────────────────

interface ActionCardProps {
  file: FileRow;
  onAction: () => void;
  showToast: (t: ToastState) => void;
}

function NewFileCard({ file, onAction, showToast }: ActionCardProps): JSX.Element {
  const [selectedType, setSelectedType] = useState<string>(file.file_type || '');

  async function handleAssign(): Promise<void> {
    if (!selectedType) return;
    const rpcResult = await callRpc('update_file_status', {
      p_file_id: file.id,
      p_new_status: 'awaiting_prep',
      p_file_type: selectedType,
    });
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Save failed' });
      return;
    }
    showToast({ kind: 'success', message: 'File moved to Awaiting Prep' });
    onAction();
  }

  return (
    <FileCard file={file}>
      <ArbiSelect
        value={selectedType}
        options={[...FILE_TYPE_OPTIONS]}
        onChange={(v) => setSelectedType(String(v))}
        placeholder="Assign type..."
        className="w-44"
        size="xs"
      />
      <button
        onClick={handleAssign}
        disabled={!selectedType}
        className={clsx(
          'arbi-btn-primary text-xs px-3 py-1.5',
          !selectedType && 'opacity-50 cursor-not-allowed'
        )}
      >
        <Check size={12} /> Assign
      </button>
    </FileCard>
  );
}

function AwaitingPrepCard({ file, onAction, showToast }: ActionCardProps): JSX.Element {
  async function handleReady(): Promise<void> {
    const rpcResult = await callRpc('update_file_status', {
      p_file_id: file.id,
      p_new_status: 'ready',
    });
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Save failed' });
      return;
    }
    showToast({ kind: 'success', message: 'File marked as Ready' });
    onAction();
  }
  return (
    <FileCard file={file}>
      <button onClick={handleReady} className="arbi-btn-primary text-xs px-3 py-1.5">
        Mark as Ready
      </button>
    </FileCard>
  );
}

function ReadyCard({ file, onAction, showToast }: ActionCardProps): JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [sentTo, setSentTo] = useState<string>('');
  const [sentDate, setSentDate] = useState<string>(new Date().toISOString().split('T')[0]);

  async function handleSend(): Promise<void> {
    if (!sentTo.trim()) return;
    const rpcResult = await callRpc('update_file_status', {
      p_file_id: file.id,
      p_new_status: 'sent',
      p_sent_to: sentTo.trim(),
      p_sent_at: new Date(sentDate).toISOString(),
    });
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Save failed' });
      return;
    }
    showToast({ kind: 'success', message: 'File marked as Sent' });
    onAction();
  }

  return (
    <div className="bg-surface-1 border border-border rounded-lg">
      <div className="p-4 flex items-center gap-4">
        {(() => {
          const Icon = getFileIcon(file.file_type);
          return <Icon size={32} className="text-ink-muted flex-shrink-0" />;
        })()}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink-bright truncate">{file.display_name}</div>
          <div className="flex items-center gap-3 mt-1 text-xs text-ink-muted">
            {file.file_size != null && file.file_size > 0 && (
              <span className="font-mono">{formatFileSize(file.file_size)}</span>
            )}
            <span className="font-mono">{formatRelativeDate(file.uploaded_at)}</span>
            <FileTypeBadge fileType={file.file_type} />
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="arbi-btn-primary text-xs px-3 py-1.5"
        >
          Mark as Sent {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-border-subtle space-y-3">
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <div>
              <label
                htmlFor={`ready-sent-to-${file.id}`}
                className="block text-xs font-medium text-ink-secondary mb-1"
              >
                Sent to
              </label>
              <input
                id={`ready-sent-to-${file.id}`}
                value={sentTo}
                onChange={(e) => setSentTo(e.target.value)}
                className="arbi-input w-full text-xs"
                placeholder="e.g. Algopix, BLVS"
              />
            </div>
            <div>
              <label
                htmlFor={`ready-sent-date-${file.id}`}
                className="block text-xs font-medium text-ink-secondary mb-1"
              >
                Date sent
              </label>
              <input
                id={`ready-sent-date-${file.id}`}
                type="date"
                value={sentDate}
                onChange={(e) => setSentDate(e.target.value)}
                className="arbi-input w-full text-xs"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSend}
              disabled={!sentTo.trim()}
              className="arbi-btn-primary text-xs px-3 py-1.5"
            >
              <Check size={12} /> Confirm
            </button>
            <button
              onClick={() => setExpanded(false)}
              className="arbi-btn-ghost text-xs px-3 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SentCard({ file, onAction, showToast }: ActionCardProps): JSX.Element {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [outputFile, setOutputFile] = useState<File | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { user } = useApp();

  async function handleOutputReceived(): Promise<void> {
    if (!outputFile) return;
    setUploading(true);
    try {
      const ts = Date.now();
      const storagePath = `outputs/${ts}-${outputFile.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('arbi-files')
        .upload(storagePath, outputFile);
      if (uploadErr) throw new Error(uploadErr.message);

      // Determine the output file type
      const outputType =
        file.file_type === 'supplier_price_sheet' ? 'algopix_output' : file.file_type;

      // Create new files record for the output
      const { error: insertErr } = await supabase.from('files').insert({
        display_name: outputFile.name,
        storage_path: storagePath,
        file_size: outputFile.size,
        mime_type: outputFile.type,
        file_type: outputType,
        lifecycle_status: 'output_received',
        uploaded_by: user?.id,
        notes: notes || null,
      } as never);
      if (insertErr) {
        // Cluster 3c (FIX Session 2) — storage + Postgres can't share a transaction,
        // so compensate: delete the just-uploaded object so a failed files-row insert
        // leaves NO orphan storage object (best-effort; the throw still surfaces).
        await supabase.storage.from('arbi-files').remove([storagePath]);
        throw new Error(insertErr.message);
      }

      // Move original sent file to imported
      const moveRpcResult = await callRpc('update_file_status', {
        p_file_id: file.id,
        p_new_status: 'imported',
      });
      const moveEnv = extractEnvelope<unknown>(moveRpcResult);
      if (!moveEnv.isOk) {
        showToast({
          kind: 'error',
          message: moveEnv.userErrors[0]?.message ?? 'Save failed',
        });
        return;
      }

      if (user?.id) {
        createNotification({
          userId: user.id,
          event: 'file_uploaded',
          title: `File uploaded: ${outputFile.name}`,
          message: `Output file received against ${file.display_name}.`,
          link: '/files',
        });
      }

      showToast({ kind: 'success', message: 'Output file uploaded and ready to import' });
      onAction();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      showToast({ kind: 'error', message: msg });
    }
    setUploading(false);
  }

  return (
    <div className="bg-surface-1 border border-border rounded-lg">
      <div className="p-4 flex items-center gap-4">
        {(() => {
          const Icon = getFileIcon(file.file_type);
          return <Icon size={32} className="text-ink-muted flex-shrink-0" />;
        })()}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink-bright truncate">{file.display_name}</div>
          <div className="flex items-center gap-3 mt-1 text-xs text-ink-muted">
            <span>
              Sent to: <span className="text-ink-secondary">{file.sent_to || '—'}</span>
            </span>
            {file.sent_at && <span className="font-mono">{formatRelativeDate(file.sent_at)}</span>}
            <FileTypeBadge fileType={file.file_type} />
          </div>
        </div>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="arbi-btn-primary text-xs px-3 py-1.5"
        >
          Mark Output Received {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t border-border-subtle space-y-3">
          <div>
            <label
              htmlFor={`sent-output-${file.id}`}
              className="block text-xs font-medium text-ink-secondary mb-1"
            >
              Upload output file
            </label>
            <input
              id={`sent-output-${file.id}`}
              ref={inputRef}
              type="file"
              accept=".xlsx,.csv"
              className="hidden"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                if (e.target.files?.[0]) setOutputFile(e.target.files[0]);
              }}
            />
            <button
              onClick={() => inputRef.current?.click()}
              className="arbi-btn-ghost text-xs px-3 py-1.5"
            >
              <Upload size={12} /> {outputFile ? outputFile.name : 'Choose file...'}
            </button>
          </div>
          <div className="max-w-sm">
            <label
              htmlFor={`sent-notes-${file.id}`}
              className="block text-xs font-medium text-ink-secondary mb-1"
            >
              Notes (optional)
            </label>
            <input
              id={`sent-notes-${file.id}`}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="arbi-input w-full text-xs"
              placeholder="Optional notes"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleOutputReceived}
              disabled={!outputFile || uploading}
              className={clsx(
                'arbi-btn-primary text-xs px-3 py-1.5',
                (!outputFile || uploading) && 'opacity-50'
              )}
            >
              {uploading ? (
                <>
                  <RefreshCw size={12} className="animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Check size={12} /> Upload & Continue
                </>
              )}
            </button>
            <button
              onClick={() => setExpanded(false)}
              className="arbi-btn-ghost text-xs px-3 py-1.5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OutputReceivedCard({ file, onAction, showToast }: ActionCardProps): JSX.Element {
  const { user, activeDistributor } = useApp();
  const algopix = useImport();
  const [importing, setImporting] = useState<boolean>(false);
  const [configOpen, setConfigOpen] = useState<boolean>(false);

  // PO assignment state (for distributor invoices)
  const [poExpanded, setPoExpanded] = useState<boolean>(false);
  const [poOptions, setPoOptions] = useState<PoOption[]>([]);
  const [selectedPo, setSelectedPo] = useState<string>('');
  const [loadingPos, setLoadingPos] = useState<boolean>(false);

  // Brand pre-flight (Day 53 — interpretation B). Parses the algopix output
  // sheet pre-import, extracts distinct brand strings, classifies each as
  // matched (case-insensitive trim against brands.name) or unmatched.
  // Reuses Day 52 match logic in dry-run mode. Bulk-create inserts missing
  // brands as auto_seeded=false so match_product_brands resolves at import.
  const [brandPreviewOpen, setBrandPreviewOpen] = useState<boolean>(false);
  const [brandPreviewLoading, setBrandPreviewLoading] = useState<boolean>(false);
  const [brandPreview, setBrandPreview] = useState<{
    matched: string[];
    unmatched: string[];
  } | null>(null);
  const [creatingBrands, setCreatingBrands] = useState<boolean>(false);

  // Find the canonical header row in an algopix output matrix using the
  // same sentinel set as `useImport.importFile`. Kept narrow here
  // because importFile's logic is private; duplication is small enough
  // that pulling it into a shared module is its own follow-up.
  function findHeaderRowIdx(matrix: unknown[][]): number {
    const SENTINELS = new Set<string>([
      'phrase',
      'upc',
      'asin',
      'ean',
      'title',
      'brand',
      'itemcost',
      'item cost',
      'cost',
      'amazonbuyboxprice',
      'amazon buy box price',
      'amazonsearchedasin',
      'identifieditemtitle',
      'algopixrecommendation',
      'algopix recommendation',
    ]);
    for (let i = 0; i < Math.min(matrix.length, 10); i++) {
      const row = matrix[i] ?? [];
      const hit = row.some((c) => c != null && SENTINELS.has(String(c).toLowerCase().trim()));
      if (hit) return i;
    }
    return 0;
  }

  async function runBrandCheck(): Promise<void> {
    if (!file.storage_path) {
      showToast({ kind: 'error', message: 'No storage path - cannot download file' });
      return;
    }
    setBrandPreviewLoading(true);
    try {
      const { data: blob, error: dlErr } = await supabase.storage
        .from('arbi-files')
        .download(file.storage_path);
      if (dlErr) throw new Error(dlErr.message);
      if (!blob) throw new Error('Download returned empty payload');

      const buffer = await blob.arrayBuffer();
      const sheets = detectSheets(buffer);
      const sheetName = sheets[0];
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) throw new Error(`Sheet "${sheetName}" not found`);

      const matrix = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: null,
        raw: true,
      }) as unknown[][];
      const headerIdx = findHeaderRowIdx(matrix);
      const headerRow = (matrix[headerIdx] ?? []).map((c) => (c == null ? '' : String(c)));
      const colFieldMap = buildColumnIndex(headerRow);
      const brandColIdx = colFieldMap.indexOf('brand');
      if (brandColIdx === -1) {
        showToast({ kind: 'error', message: 'No brand column found in sheet' });
        setBrandPreview({ matched: [], unmatched: [] });
        return;
      }

      // Extract distinct trimmed brand strings from data rows.
      const distinctBrands = new Set<string>();
      for (let r = headerIdx + 1; r < matrix.length; r++) {
        const row = matrix[r] ?? [];
        const cell = row[brandColIdx];
        const brand = cell == null ? '' : String(cell).trim();
        if (brand) distinctBrands.add(brand);
      }

      if (distinctBrands.size === 0) {
        setBrandPreview({ matched: [], unmatched: [] });
        showToast({ kind: 'error', message: 'No brand strings found in sheet' });
        return;
      }

      // Pull all existing brand names; case-insensitive trim match.
      const { data: brandRows, error: brErr } = await supabase.from('brands').select('name');
      if (brErr) throw new Error(brErr.message);
      const existingLower = new Set<string>(
        ((brandRows ?? []) as Array<{ name: string | null }>).map((b) =>
          (b.name ?? '').trim().toLowerCase()
        )
      );

      const matched: string[] = [];
      const unmatched: string[] = [];
      for (const b of distinctBrands) {
        if (existingLower.has(b.toLowerCase())) {
          matched.push(b);
        } else {
          unmatched.push(b);
        }
      }
      matched.sort();
      unmatched.sort();
      setBrandPreview({ matched, unmatched });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      showToast({ kind: 'error', message: `Brand check failed - ${msg}` });
    } finally {
      setBrandPreviewLoading(false);
    }
  }

  async function bulkCreateMissingBrands(): Promise<void> {
    if (!brandPreview || brandPreview.unmatched.length === 0) return;
    setCreatingBrands(true);
    try {
      const payload = brandPreview.unmatched.map((name) => ({
        name,
        auto_seeded: false,
        is_active: true,
        // Day 82 P6.1 — auto_seeded + is_active envelopes (both explicit
        // app writes per L11 tracked fields). L5 split: app writes source +
        // reason; trigger enriches set_at + set_by.
        provenance: {
          auto_seeded: { source: PROVENANCE_SOURCE_ALGOPIX_DISCOVERY, reason: null },
          is_active: { source: PROVENANCE_SOURCE_ALGOPIX_DISCOVERY, reason: null },
        },
      }));
      const { error } = await supabase.from('brands').insert(payload as never);
      if (error) {
        showToast({ kind: 'error', message: `Bulk create failed - ${error.message}` });
        return;
      }
      showToast({
        kind: 'success',
        message: `${payload.length} brand${payload.length === 1 ? '' : 's'} created`,
      });
      // Re-run the check so the panel reflects the new state (all should be matched).
      await runBrandCheck();
    } finally {
      setCreatingBrands(false);
    }
  }

  async function handleImport(
    manufacturerId: string,
    markupOverride: number | null,
    sheetMode: SheetMode = 'price_sheet',
    upstreamSourceText: string | null = null
  ): Promise<void> {
    if (!file.storage_path) {
      showToast({ kind: 'error', message: 'No storage path — cannot download file' });
      return;
    }
    setImporting(true);
    try {
      // Download from Supabase Storage
      const { data: blob, error: dlErr } = await supabase.storage
        .from('arbi-files')
        .download(file.storage_path);
      if (dlErr) throw new Error(dlErr.message);
      if (!blob) throw new Error('Download returned empty payload');

      const buffer = await blob.arrayBuffer();
      const sheets = detectSheets(buffer);
      const sheetName = sheets[0]; // Use first sheet

      await algopix.importFile(
        file.display_name ?? '',
        buffer,
        sheetName,
        activeDistributor?.id ?? null,
        manufacturerId,
        user?.id ?? null,
        markupOverride,
        sheetMode,
        upstreamSourceText
      );

      // Check result
      if (algopix.status === 'complete' && algopix.summary) {
        // Update file to imported and link to ingestion_batches
        const importedRpcResult = await callRpc('update_file_status', {
          p_file_id: file.id,
          p_new_status: 'imported',
        });
        const importedEnv = extractEnvelope<unknown>(importedRpcResult);
        if (!importedEnv.isOk) {
          showToast({
            kind: 'error',
            message: importedEnv.userErrors[0]?.message ?? 'Save failed',
          });
        } else {
          showToast({
            kind: 'success',
            message: `Import complete — ${algopix.summary.inserted + algopix.summary.updated} products processed${algopix.summary.held > 0 ? `, ${algopix.summary.held} held for review` : ''}`,
          });
        }
      } else if (algopix.status === 'error') {
        const issuesRpcResult = await callRpc('update_file_status', {
          p_file_id: file.id,
          p_new_status: 'issues',
          p_error_log: algopix.errors?.[0]?.friendly || 'Import failed',
        });
        const issuesEnv = extractEnvelope<unknown>(issuesRpcResult);
        if (!issuesEnv.isOk) {
          showToast({
            kind: 'error',
            message:
              issuesEnv.userErrors[0]?.message ?? 'Import failed and could not move to Issues tab',
          });
        } else {
          showToast({ kind: 'error', message: 'Import failed — moved to Issues tab' });
        }
      }
      onAction();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      showToast({ kind: 'error', message: msg });
      // Best-effort status-cleanup after caught error; envelope failure here
      // does not re-toast (the caught error is the user-facing notification).
      const cleanupRpcResult = await callRpc('update_file_status', {
        p_file_id: file.id,
        p_new_status: 'issues',
        p_error_log: msg,
      });
      const cleanupEnv = extractEnvelope<unknown>(cleanupRpcResult);
      if (!cleanupEnv.isOk) {
        console.error(
          '[Files] post-catch status cleanup failed:',
          cleanupEnv.userErrors[0]?.message
        );
      }
      onAction();
    }
    setImporting(false);
  }

  async function loadPOs(): Promise<void> {
    setLoadingPos(true);
    const { data } = await supabase
      .from('purchase_orders')
      .select('id, po_number, status')
      .in('status', ['approved', 'ordered', 'awaiting_invoice', 'processing'])
      .order('created_at', { ascending: false })
      .limit(50);
    const rows = (data ?? []) as Array<{
      id: string;
      po_number: string | null;
      status: string | null;
    }>;
    setPoOptions(
      rows.map((po) => ({
        value: po.id,
        label: `${po.po_number ?? ''} (${po.status ?? ''})`,
      }))
    );
    setLoadingPos(false);
  }

  async function handleAssignPO(): Promise<void> {
    if (!selectedPo) return;
    const rpcResult = await callRpc('update_file_status', {
      p_file_id: file.id,
      p_new_status: 'imported',
      p_related_po_id: selectedPo,
    });
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Save failed' });
      return;
    }
    const poLabel = poOptions.find((o) => o.value === selectedPo)?.label || selectedPo;
    showToast({ kind: 'success', message: `Invoice assigned to ${poLabel}` });
    onAction();
  }

  async function handleMarkImported(): Promise<void> {
    const rpcResult = await callRpc('update_file_status', {
      p_file_id: file.id,
      p_new_status: 'imported',
    });
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Save failed' });
      return;
    }
    showToast({ kind: 'success', message: 'File marked as imported' });
    onAction();
  }

  const isAlgopixOutput = file.file_type === 'algopix_output';
  const isInvoice = file.file_type === 'distributor_invoice';

  return (
    <div className="bg-surface-1 border border-border rounded-lg">
      <div className="p-4 flex items-center gap-4">
        {(() => {
          const Icon = getFileIcon(file.file_type);
          return <Icon size={32} className="text-ink-muted flex-shrink-0" />;
        })()}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-ink-bright truncate">{file.display_name}</div>
          <div className="flex items-center gap-3 mt-1 text-xs text-ink-muted">
            <span className="font-mono">{formatRelativeDate(file.uploaded_at)}</span>
            <FileTypeBadge fileType={file.file_type} />
          </div>
        </div>
        {isAlgopixOutput && (
          <>
            <button
              onClick={() => {
                const next = !brandPreviewOpen;
                setBrandPreviewOpen(next);
                if (next && !brandPreview && !brandPreviewLoading) {
                  void runBrandCheck();
                }
              }}
              disabled={importing}
              title="Check which brand strings in this sheet match an existing brand"
              className="arbi-btn-ghost text-xs px-2 py-1.5 flex items-center gap-1"
            >
              <Tag size={12} />
              {brandPreviewOpen ? 'Hide brands' : 'Check brands'}
              {brandPreview && brandPreview.unmatched.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded text-2xs bg-warning/15 text-warning font-mono">
                  {brandPreview.unmatched.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setConfigOpen(true)}
              disabled={importing}
              className="arbi-btn-primary text-xs px-3 py-1.5"
            >
              {importing ? (
                <>
                  <RefreshCw size={12} className="animate-spin" /> Importing...
                </>
              ) : (
                'Import'
              )}
            </button>
          </>
        )}
        {isInvoice && (
          <button
            onClick={() => {
              setPoExpanded((v) => !v);
              if (!poExpanded) loadPOs();
            }}
            className="arbi-btn-primary text-xs px-3 py-1.5"
          >
            Assign to PO {poExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}
        {!isAlgopixOutput && !isInvoice && (
          <button onClick={handleMarkImported} className="arbi-btn-ghost text-xs px-3 py-1.5">
            Mark as Imported
          </button>
        )}
      </div>

      {/* Import progress */}
      {importing && algopix.status === 'processing' && algopix.progress.total > 0 && (
        <div className="px-4 pb-3">
          <div className="w-full bg-surface-2 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-accent h-1.5 rounded-full transition-all"
              style={{ width: `${algopix.progress.pct}%` }}
            />
          </div>
          <div className="text-xs text-ink-muted mt-1 font-mono">
            {algopix.progress.current.toLocaleString()} / {algopix.progress.total.toLocaleString()}{' '}
            rows
          </div>
        </div>
      )}

      {/* W1 — ingest config: manufacturer + markup + column-map/cost-confirm gate */}
      <ImportConfigModal
        open={configOpen}
        file={{
          id: file.id,
          display_name: file.display_name ?? null,
          storage_path: file.storage_path ?? null,
        }}
        distributorId={activeDistributor?.id ?? null}
        distributorName={activeDistributor?.name ?? ''}
        onCancel={() => setConfigOpen(false)}
        onConfirm={(manufacturerId, markupOverride, sheetMode, upstreamSourceText) => {
          setConfigOpen(false);
          void handleImport(manufacturerId, markupOverride, sheetMode, upstreamSourceText);
        }}
        showToast={showToast}
      />

      {/* Brand pre-flight panel */}
      {isAlgopixOutput && brandPreviewOpen && (
        <div className="px-4 pb-4 pt-2 border-t border-border-subtle space-y-3">
          {brandPreviewLoading ? (
            <div className="text-xs text-ink-muted flex items-center gap-2">
              <RefreshCw size={12} className="animate-spin" /> Reading brand column...
            </div>
          ) : brandPreview === null ? (
            <p className="text-xs text-ink-muted">Click Check brands to load.</p>
          ) : (
            <>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-ink-muted">Brand strings in this sheet:</span>
                <span className="font-mono text-success">
                  {brandPreview.matched.length} matched
                </span>
                <span className="font-mono text-warning">
                  {brandPreview.unmatched.length} unmatched
                </span>
              </div>
              {brandPreview.unmatched.length === 0 ? (
                <p className="text-xs text-success flex items-center gap-1">
                  <CheckCircle size={12} /> All brand strings already exist in the directory. Import
                  will resolve cleanly.
                </p>
              ) : (
                <>
                  <p className="text-xs text-ink-secondary">
                    The following brand strings will land as <code>unknown_brand</code> flags after
                    import unless their entities exist. Create them now to auto-resolve at import
                    time:
                  </p>
                  <div className="max-h-40 overflow-y-auto bg-surface-2 border border-border-subtle rounded p-2">
                    <div className="flex flex-wrap gap-1">
                      {brandPreview.unmatched.map((b) => (
                        <span
                          key={b}
                          className="px-1.5 py-0.5 rounded text-2xs font-mono bg-surface-3 text-ink-secondary"
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={bulkCreateMissingBrands}
                      disabled={creatingBrands}
                      className={clsx(
                        'arbi-btn-primary text-xs px-3 py-1.5 flex items-center gap-1',
                        creatingBrands && 'opacity-60 cursor-not-allowed'
                      )}
                    >
                      {creatingBrands ? (
                        <>
                          <RefreshCw size={12} className="animate-spin" /> Creating...
                        </>
                      ) : (
                        <>
                          <Tag size={12} /> Create {brandPreview.unmatched.length} brand
                          {brandPreview.unmatched.length === 1 ? '' : 's'}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => void runBrandCheck()}
                      disabled={brandPreviewLoading}
                      className="arbi-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1"
                    >
                      <RefreshCw size={12} /> Re-check
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* PO assignment inline form */}
      {poExpanded && (
        <div className="px-4 pb-4 pt-2 border-t border-border-subtle space-y-3">
          {loadingPos ? (
            <div className="text-xs text-ink-muted flex items-center gap-2">
              <RefreshCw size={12} className="animate-spin" /> Loading POs...
            </div>
          ) : poOptions.length === 0 ? (
            <p className="text-xs text-ink-muted">No eligible POs found</p>
          ) : (
            <>
              <div className="max-w-sm">
                <label
                  htmlFor={`output-po-${file.id}`}
                  className="block text-xs font-medium text-ink-secondary mb-1"
                >
                  Select Purchase Order
                </label>
                <div id={`output-po-${file.id}`}>
                  <ArbiSelect
                    value={selectedPo}
                    options={poOptions}
                    onChange={(v) => setSelectedPo(String(v))}
                    placeholder="Select PO..."
                    className="w-full"
                    size="xs"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAssignPO}
                  disabled={!selectedPo}
                  className={clsx(
                    'arbi-btn-primary text-xs px-3 py-1.5',
                    !selectedPo && 'opacity-50'
                  )}
                >
                  <Check size={12} /> Assign
                </button>
                <button
                  onClick={() => setPoExpanded(false)}
                  className="arbi-btn-ghost text-xs px-3 py-1.5"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface ImportedCardProps {
  file: FileRow;
  liveCount: number | null | undefined;
  canArm?: boolean;
  showToast?: (t: ToastState) => void;
}

function ImportedCard({
  file,
  liveCount,
  canArm = false,
  showToast,
}: ImportedCardProps): JSX.Element {
  const navigate = useNavigate();
  const count = liveCount ?? file.import_processed_count;
  const [arming, setArming] = useState<boolean>(false);

  // W4b-β2 — arm the import for the match pipeline (the §6 "ready for match + data" affordance).
  // request_match_run stamps ingestion_batches.match_requested_at; the acquisition orchestrator (or a
  // manual drain) picks it up. Admin/user only (canArm = !isViewer && !isDistRep at the call site).
  async function handleArm(): Promise<void> {
    if (!file.related_import_id) return;
    setArming(true);
    const rpcResult = await callRpc('request_match_run', { p_import_id: file.related_import_id });
    setArming(false);
    const env = extractEnvelope<{ import_id: string; armed_at: string }>(rpcResult);
    if (!env.isOk) {
      showToast?.({
        kind: 'error',
        message: env.userErrors[0]?.message ?? 'Could not arm this import',
      });
      return;
    }
    showToast?.({
      kind: 'success',
      message: 'Import armed for match — the resolver will pick it up.',
    });
  }

  return (
    <FileCard file={file}>
      {file.file_type === 'algopix_output' && file.related_import_id && (
        <span className="text-xs text-ink-muted font-mono">
          {count != null
            ? count === 1
              ? '1 product'
              : `${count.toLocaleString()} products`
            : '— products'}
        </span>
      )}
      {canArm && file.file_type === 'algopix_output' && file.related_import_id && (
        <button
          onClick={() => void handleArm()}
          disabled={arming}
          className="text-xs text-accent hover:text-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {arming ? 'Arming…' : 'Arm for Match'}
        </button>
      )}
      {file.file_type === 'distributor_invoice' && file.po_number && (
        <button
          onClick={() => navigate('/purchasing/purchase-orders')}
          className="text-xs text-accent hover:text-accent-hover transition-colors flex items-center gap-1"
        >
          PO: {file.po_number} <ExternalLink size={10} />
        </button>
      )}
    </FileCard>
  );
}

function IssuesCard({ file, onAction, showToast }: ActionCardProps): JSX.Element {
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  async function handleRetry(): Promise<void> {
    const rpcResult = await callRpc('update_file_status', {
      p_file_id: file.id,
      p_new_status: 'output_received',
      p_error_log: '',
    });
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Save failed' });
      return;
    }
    showToast({ kind: 'success', message: 'File moved back to Output Received' });
    onAction();
  }

  async function handleDismiss(): Promise<void> {
    const rpcResult = await callRpc('update_file_status', {
      p_file_id: file.id,
      p_new_status: 'imported',
    });
    const env = extractEnvelope<unknown>(rpcResult);
    if (!env.isOk) {
      showToast({ kind: 'error', message: env.userErrors[0]?.message ?? 'Save failed' });
      return;
    }
    showToast({ kind: 'success', message: 'File dismissed from Issues' });
    setConfirmOpen(false);
    onAction();
  }

  return (
    <>
      <div className="bg-surface-1 border border-border rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-4">
          <AlertTriangle size={32} className="text-danger flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-ink-bright truncate">{file.display_name}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-ink-muted">
              <span className="font-mono">{formatRelativeDate(file.uploaded_at)}</span>
              <FileTypeBadge fileType={file.file_type} />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {file.file_type === 'algopix_output' && (
              <button onClick={handleRetry} className="arbi-btn-ghost text-xs px-3 py-1.5">
                <RotateCcw size={12} /> Retry
              </button>
            )}
            <button
              onClick={() => setConfirmOpen(true)}
              className="arbi-btn-ghost text-xs px-3 py-1.5 text-danger"
            >
              <Trash2 size={12} /> Dismiss
            </button>
          </div>
        </div>
        {file.error_log && (
          <div className="bg-surface-2 rounded-md p-3">
            <p className="text-xs font-mono text-danger whitespace-pre-wrap">{file.error_log}</p>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Dismiss this file?"
        message="The file record will be kept but moved out of Issues. This cannot be undone."
        onConfirm={handleDismiss}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

// ── Empty states ──────────────────────────────────────────────────────────────

interface EmptyStateConfig {
  icon: LucideIcon;
  text: string;
  cta: string | null;
  action?: () => void;
}

interface EmptyStateProps {
  tab: TabKey;
  onUpload: () => void;
  readOnly?: boolean;
}

function EmptyState({ tab, onUpload, readOnly = false }: EmptyStateProps): JSX.Element {
  const messages: Record<TabKey, EmptyStateConfig> = {
    new: readOnly
      ? { icon: Upload, text: 'No files in this stage', cta: null }
      : {
          icon: Upload,
          text: 'No files yet — upload your first supplier price sheet',
          cta: 'Upload File',
          action: onUpload,
        },
    awaiting_prep: { icon: Clock, text: 'No files awaiting prep', cta: null },
    ready: { icon: Check, text: 'No files ready to send', cta: null },
    sent: { icon: FileOutput, text: 'No files have been sent', cta: null },
    output_received: { icon: FileSpreadsheet, text: 'No output files received yet', cta: null },
    imported: { icon: CheckCircle, text: 'No files have been imported yet', cta: null },
    issues: { icon: AlertTriangle, text: 'No issues — everything is running smoothly', cta: null },
  };
  const cfg = messages[tab] || messages.new;
  const Icon = cfg.icon;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <Icon size={48} className="text-ink-muted mb-3" />
      <p className="text-sm text-ink-muted">{cfg.text}</p>
      {cfg.cta && cfg.action && (
        <button onClick={cfg.action} className="arbi-btn-primary text-sm mt-4">
          <Upload size={14} /> {cfg.cta}
        </button>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Files(): JSX.Element {
  const { user, isViewer, isDistRep, profileDistributorId } = useApp();

  const [activeTab, setActiveTab] = useState<TabKey>('new');
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [filesData, setFilesData] = useState<Record<string, FileRow[]>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [importLiveCounts, setImportLiveCounts] = useState<Record<string, number>>({});

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const showToast = useCallback((t: ToastState): void => setToast(t), []);

  // Load status counts
  const loadCounts = useCallback(async (): Promise<void> => {
    const { data } = await callRpc('get_file_status_counts');
    if (data) {
      const rows = data as Array<{ lifecycle_status: string; count: number | string }>;
      const counts: Record<string, number> = {};
      rows.forEach((row) => {
        counts[row.lifecycle_status] = Number(row.count);
      });
      setStatusCounts(counts);
    }
  }, []);

  // Load files for a tab
  const loadTab = useCallback(
    async (tabKey: TabKey): Promise<void> => {
      setLoading(true);
      const { data, error } = await callRpc('get_files_by_status', { p_status: tabKey });
      if (error) {
        showToast({ kind: 'error', message: error.message });
        setLoading(false);
        return;
      }
      const rows = (data as FileRow[]) || [];
      setFilesData((prev) => ({ ...prev, [tabKey]: rows }));
      setLoading(false);

      // For imported tab, load live product counts
      if (tabKey === 'imported' && rows.length) {
        const importIds = rows
          .filter((f) => f.related_import_id)
          .map((f) => f.related_import_id as string);
        if (importIds.length > 0) {
          const { data: products } = await supabase
            .from('products')
            .select('import_id')
            .in('import_id', importIds);
          if (products) {
            const productRows = products as Array<{ import_id: string | null }>;
            const counts: Record<string, number> = {};
            for (const p of productRows) {
              if (!p.import_id) continue;
              counts[p.import_id] = (counts[p.import_id] || 0) + 1;
            }
            setImportLiveCounts(counts);
          }
        }
      }
    },
    [showToast]
  );

  // Initial load
  useEffect(() => {
    loadCounts();
    loadTab('new');
  }, [loadCounts, loadTab]);

  // Load tab data when switching
  function switchTab(tabKey: TabKey): void {
    setActiveTab(tabKey);
    loadTab(tabKey);
  }

  // Refresh after any action
  function handleAction(): void {
    loadCounts();
    loadTab(activeTab);
  }

  // Upload handler
  async function handleUpload(file: File | null | undefined): Promise<void> {
    if (!file) return;
    setUploading(true);
    try {
      const ts = Date.now();
      const storagePath = `uploads/${user?.id}/${ts}-${file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('arbi-files')
        .upload(storagePath, file);
      if (uploadErr) throw new Error(uploadErr.message);

      const { error: insertErr } = await supabase.from('files').insert({
        display_name: file.name,
        storage_path: storagePath,
        file_size: file.size,
        mime_type: file.type,
        lifecycle_status: 'new',
        uploaded_by: user?.id,
      } as never);
      if (insertErr) throw new Error(insertErr.message);

      if (user?.id) {
        createNotification({
          userId: user.id,
          event: 'file_uploaded',
          title: `File uploaded: ${file.name}`,
          message: 'Assign a type to continue the lifecycle.',
          link: '/files',
        });
      }

      showToast({ kind: 'success', message: 'File uploaded — assign a type to continue' });
      switchTab('new');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      showToast({ kind: 'error', message: msg });
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleUploadClick(): void {
    fileInputRef.current?.click();
  }

  const currentFiles: FileRow[] = filesData[activeTab] || [];

  return (
    <div className="h-full overflow-auto">
      <Toast toast={toast} onDismiss={() => setToast(null)} />

      {/* Hidden file input — admin/user only */}
      {!isDistRep && (
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.csv,.pdf"
          aria-label="Choose file to upload"
          className="hidden"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.files?.[0]) handleUpload(e.target.files[0]);
          }}
        />
      )}

      <div className="max-w-5xl mx-auto p-5">
        {isDistRep && !profileDistributorId && (
          <div className="mb-4 px-4 py-3 bg-danger/10 border border-danger/30 rounded-lg text-sm text-danger">
            Your account is not assigned to a distributor — contact your administrator.
          </div>
        )}

        {/* Page header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-ink-bright">Files</h1>
            <p className="text-sm text-ink-muted mt-1">
              {isDistRep
                ? 'Read-only view of files for your distributor'
                : 'Manage supplier price sheets, analysis outputs, and distributor invoices'}
            </p>
          </div>
          {!isDistRep && (
            <button
              onClick={handleUploadClick}
              disabled={uploading}
              className="arbi-btn-primary flex items-center gap-2"
            >
              {uploading ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
          )}
        </div>

        {/* Tab bar */}
        <div
          role="tablist"
          aria-label="File lifecycle stages"
          className="flex items-center gap-0 border-b border-border mb-6 overflow-x-auto"
        >
          {TABS.map((tab) => {
            const count = statusCounts[tab.key] || 0;
            const isActive = activeTab === tab.key;
            const isIssues = tab.key === 'issues';
            return (
              <button
                key={tab.key}
                role="tab"
                aria-selected={isActive}
                aria-label={tab.label}
                onClick={() => switchTab(tab.key)}
                className={clsx(
                  'px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors whitespace-nowrap flex items-center gap-1.5',
                  isActive
                    ? 'border-accent text-accent'
                    : 'border-transparent text-ink-muted hover:text-ink-secondary'
                )}
              >
                {tab.label}
                {count > 0 && (
                  <span
                    className={clsx(
                      'px-1.5 py-0.5 rounded-full text-2xs font-mono font-medium',
                      isIssues && count > 0
                        ? 'bg-danger/15 text-danger'
                        : 'bg-accent/15 text-accent'
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* File list */}
        {loading ? (
          <div className="space-y-3">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : currentFiles.length === 0 ? (
          <EmptyState tab={activeTab} onUpload={handleUploadClick} readOnly={isDistRep} />
        ) : (
          <div className="space-y-3">
            {currentFiles.map((file) => {
              // dist_rep is read-only: render the base FileCard with no
              // mutation affordances (no Assign type, no Mark Ready, no
              // Mark Sent, no Output upload, no Import, no Retry, no Dismiss).
              // Imported tab still shows the live product count via ImportedCard.
              if (isDistRep) {
                if (activeTab === 'imported') {
                  return (
                    <ImportedCard
                      key={file.id}
                      file={file}
                      liveCount={
                        file.related_import_id ? importLiveCounts[file.related_import_id] : null
                      }
                    />
                  );
                }
                return <FileCard key={file.id} file={file} />;
              }

              switch (activeTab) {
                case 'new':
                  return (
                    <NewFileCard
                      key={file.id}
                      file={file}
                      onAction={handleAction}
                      showToast={showToast}
                    />
                  );
                case 'awaiting_prep':
                  return (
                    <AwaitingPrepCard
                      key={file.id}
                      file={file}
                      onAction={handleAction}
                      showToast={showToast}
                    />
                  );
                case 'ready':
                  return (
                    <ReadyCard
                      key={file.id}
                      file={file}
                      onAction={handleAction}
                      showToast={showToast}
                    />
                  );
                case 'sent':
                  return (
                    <SentCard
                      key={file.id}
                      file={file}
                      onAction={handleAction}
                      showToast={showToast}
                    />
                  );
                case 'output_received':
                  return (
                    <OutputReceivedCard
                      key={file.id}
                      file={file}
                      onAction={handleAction}
                      showToast={showToast}
                    />
                  );
                case 'imported':
                  return (
                    <ImportedCard
                      key={file.id}
                      file={file}
                      liveCount={
                        file.related_import_id ? importLiveCounts[file.related_import_id] : null
                      }
                      canArm={!isViewer}
                      showToast={showToast}
                    />
                  );
                case 'issues':
                  return (
                    <IssuesCard
                      key={file.id}
                      file={file}
                      onAction={handleAction}
                      showToast={showToast}
                    />
                  );
                default:
                  return <FileCard key={file.id} file={file} />;
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
}
